/**
 * Atlas PM · 任务设计器（P3.7b）· 主容器
 *
 * ⭐ P6.51：产出物契约整体取消，本设计器只剩「任务指标与完成要素」一列。
 * 创建任务时设定分类 → 指标 → 完成要素，最后一次性顺序提交；
 * 齐套率改由「要素勾选『需要附件』」的附件上传情况核算，不再声明契约。
 *
 * P6.2：本组件改为由 TaskFormDrawer 在右侧抽屉内渲染（不再有独立页面 / 路由）。
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { useAuthStore } from '../../stores/auth';
import { useUiStore } from '../../stores/ui';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Spinner from '../../components/Spinner';
import UserPicker from '../../components/UserPicker';
import type { ProjectListItem } from '@atlaspm/shared';
import { TASK_PRIORITY, priorityLabel } from '@atlaspm/shared';
import type { Paginated } from '../../api/types';
import { todayYmd, ymdPlusDays } from '../../utils/date';
import {
  countRemovals,
  fetchTaskDesign,
  isPermissionFailure,
  submitTaskDesign,
  updateTaskDesign,
  type SubmitFailure,
  type SubmitResult,
} from './api';
import './task-form.css';
import MetricDesigner from './MetricDesigner';
import TemplatePickerDialog from './TemplatePickerDialog';
import { structureToMetricDrafts, type TemplateStructure } from './taskTemplates';
import { draftKey, emptyElementDraft, emptyMetricDraft, type MetricDraft, type TaskMetaDraft } from './types';
import { useTaskCategoryOptions } from './useTaskCategoryOptions';

const PRIORITY_OPTIONS = [TASK_PRIORITY.HIGH, TASK_PRIORITY.MEDIUM, TASK_PRIORITY.LOW].map((v) => ({
  value: String(v),
  label: priorityLabel(v),
}));

/**
 * ⭐ P7.11「权重自动填满」：只对**用户尚未手动改过**（`weightTouched !== true`）的指标
 * 重新平摊权重，使「已设置 + 未设置」的合计趋近 100。
 *
 * 规则：
 *   · 未设置者平分「剩余额度 = 100 − 已设置者权重和」，整数分配、余数前置补 1，
 *     保证合计恰好 100；
 *   · 已设置者（用户显式输入过）**原样保留**，绝不被覆盖；
 *   · 全部都是未设置时，即平均分配（1 个指标 → 100；2 个 → 50/50；3 个 → 34/33/33 …）。
 *
 * ⚠️ 仅在「增 / 删 / 复制指标」时调用，不监听用户输入；用户键入权重即视为显式设置。
 */
function rebalanceUntouchedWeights(metrics: MetricDraft[]): MetricDraft[] {
  const untouched = metrics.filter((m) => !m.weightTouched);
  if (untouched.length === 0) return metrics;

  const touchedSum = metrics
    .filter((m) => m.weightTouched)
    .reduce((s, m) => s + (Number(m.weight) || 0), 0);
  const remaining = Math.max(0, 100 - touchedSum);
  const base = Math.floor(remaining / untouched.length);
  let remainder = Math.round(remaining - base * untouched.length);

  return metrics.map((m) => {
    if (m.weightTouched) return m;
    const extra = remainder > 0 ? 1 : 0;
    if (remainder > 0) remainder -= 1;
    return { ...m, weight: String(base + extra) };
  });
}

export interface TaskDesignerProps {
  /** 预选项目（抽屉打开时由 TaskFormDrawer 传入；优先于 uiStore / 列表首项） */
  projectId?: string | null;
  /**
   * ⭐ P7.5：编辑模式 —— 有值则进入「编辑任务」：
   *   先拉取任务 + 指标/要素回填，保存走差异比对（updateTaskDesign）。
   *   无值 = 维持原有「新建任务」逻辑不变。
   */
  editTaskId?: string | null;
  /**
   * ⭐ P7.14：主操作按钮（创建/保存）的 portal 挂载点 ——
   *   TaskFormDrawer 在标题行提供，本组件把按钮投递到此处，
   *   从而在**不提升状态**的前提下让按钮出现在抽屉顶部一行。
   */
  actionsTarget?: HTMLElement | null;
}

export default function TaskDesigner({
  projectId = null,
  editTaskId = null,
  actionsTarget = null,
}: TaskDesignerProps = {}) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const currentProjectId = useUiStore((s) => s.currentProjectId);
  const openTask = useUiStore((s) => s.openTask);
  const closeTaskForm = useUiStore((s) => s.closeTaskForm);
  const isEdit = Boolean(editTaskId);

  const projectsQuery = useQuery({
    queryKey: qk.projects({ purpose: 'task-designer' }),
    queryFn: () =>
      api.get<Paginated<ProjectListItem>>(`/projects${qs({ page: 1, pageSize: 100 })}`),
    staleTime: 60_000,
  });
  const projects = projectsQuery.data?.items ?? [];

  const [meta, setMeta] = useState<TaskMetaDraft>({
    projectId: '',
    name: '',
    ownerId: '',
    categoryId: '4',
    description: '',
    priority: String(TASK_PRIORITY.MEDIUM),
    // ⭐ P7.9：计划起止时间默认值 —— 开始 = 今天，结束 = 今天 + 7 天
    //    （本地年月日拼 `YYYY-MM-DD`，禁用 toISOString 以免东八区差一天；P7.8 教训）
    //    编辑模式由首屏回填覆盖（后端为空则清空为 ''，不会误带默认值）
    planStart: todayYmd(),
    planEnd: ymdPlusDays(7),
  });

  const [metrics, setMetrics] = useState<MetricDraft[]>(() => [emptyMetricDraft()]);

  /**
   * ⭐ P8.6：任务表单改读**公开只读**接口（GET /task-categories，登录即可），
   *   不再复用设置页需要 `dict:manage` 的 `/system/task-categories`。
   *   - 仅启用中（isActive）项可选，按 sortOrder / id 排序；
   *   - 当前值若指向已停用项则额外并入并标注「（已停用）」。
   */
  const { query: categoriesQuery, options: categoryOptions } = useTaskCategoryOptions(
    meta.categoryId,
    meta.categoryName,
  );
  /** ⭐ P8.9：当前选中形态的备注说明（无说明时不渲染提示行） */
  const selectedCategory = categoryOptions.find((o) => o.value === meta.categoryId);

  const [submitting, setSubmitting] = useState(false);
  /**
   * ⭐ P7.9：提交成功后的「已锁定」标志（新建 / 编辑共用）。
   *   任务创建成功后按钮不得再次可点 —— 否则用户没注意到结果面板、
   *   以为失败又点一次，就会重复创建出多条同名任务。
   *   仅在组件卸载（抽屉关闭后重开）时自然复位，故此处不做手动复位。
   */
  const [submitted, setSubmitted] = useState(false);
  /** ⭐ P8.1：从模板新建 —— 模板选择弹窗开关（仅新建模式使用） */
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [formError, setFormError] = useState<string>('');
  const [result, setResult] = useState<SubmitResult | null>(null);

  /* ⭐ P7.5 编辑模式：加载态 / 失败态 / 保存失败清单 / 删除二次确认 */
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [loadError, setLoadError] = useState<string>('');
  const [editFailures, setEditFailures] = useState<SubmitFailure[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    metricCount: number;
    elementCount: number;
  } | null>(null);
  /** 打开编辑时的原始快照（含后端 id + 用户已填完成度/质量），供差异比对 */
  const originalRef = useRef<MetricDraft[]>([]);

  const nameRef = useRef<HTMLInputElement | null>(null);

  /* 默认项目：抽屉传入 → uiStore 当前项目 → 列表第一项（⚠️ 编辑模式由回填逻辑接管） */
  useEffect(() => {
    if (isEdit) return;
    setMeta((prev) => {
      if (prev.projectId) return prev;
      const next = projectId || currentProjectId || projects[0]?.id || '';
      return next ? { ...prev, projectId: next } : prev;
    });
  }, [isEdit, projectId, currentProjectId, projects]);

  /* 默认负责人：当前登录用户（⚠️ 编辑模式保留任务原负责人，不覆盖） */
  useEffect(() => {
    if (isEdit) return;
    if (!user) return;
    setMeta((prev) => (prev.ownerId ? prev : { ...prev, ownerId: user.userId }));
  }, [isEdit, user]);

  /* ⭐ P7.5：编辑模式首屏加载 —— 拉取任务 + 指标（含要素/填写值）并回填 */
  useEffect(() => {
    if (!editTaskId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setFormError('');
    setResult(null);
    setEditFailures([]);
    fetchTaskDesign(editTaskId)
      .then((snap) => {
        if (cancelled) return;
        setMeta(snap.task);
        setMetrics(snap.metrics);
        // 深拷贝原始快照，避免后续表单编辑污染比对基准
        originalRef.current = snap.metrics.map((m) => ({
          ...m,
          elements: m.elements.map((e) => ({ ...e })),
        }));
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : '加载任务失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editTaskId]);

  /* ⭐ P7.5：编辑保存结果埋点（自测用） */
  useEffect(() => {
    if (!editFailures.length) return;
    const w = window as unknown as Record<string, unknown>;
    w.__P75_EDIT_FAILURES__ = editFailures.length;
    return () => {
      delete w.__P75_EDIT_FAILURES__;
    };
  }, [editFailures]);

  /* 测试钩子：暴露最近一次创建结果 */
  useEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    if (result) {
      w.__P37B_CREATED__ = {
        taskId: result.taskId,
        metrics: result.metricCreated,
        failures: result.failures.length,
      };
    }
    return () => {
      delete w.__P37B_CREATED__;
    };
  }, [result]);

  /* ── 指标编辑 ── */
  const changeMetric = (key: string, patch: Partial<MetricDraft>) =>
    setMetrics((prev) =>
      prev.map((m) =>
        m.key === key
          ? {
              ...m,
              ...patch,
              // ⭐ P7.11：用户一旦改动权重，即视为「显式设置」，后续自动平摊不得覆盖
              ...(patch.weight !== undefined ? { weightTouched: true } : {}),
            }
          : m,
      ),
    );
  const removeMetric = (key: string) =>
    setMetrics((prev) => rebalanceUntouchedWeights(prev.filter((m) => m.key !== key)));
  const duplicateMetric = (key: string) =>
    setMetrics((prev) => {
      const src = prev.find((m) => m.key === key);
      if (!src) return prev;
      const copy: MetricDraft = {
        ...src,
        key: draftKey('m'),
        name: `${src.name} 副本`,
        // 副本视为「未设置」，交给自动平摊，避免复制后合计超 100
        weightTouched: false,
        elements: src.elements.map((e) => ({ ...e, key: draftKey('e') })),
      };
      return rebalanceUntouchedWeights([...prev, copy]);
    });
  const addMetric = () =>
    setMetrics((prev) => rebalanceUntouchedWeights([...prev, emptyMetricDraft()]));

  const changeElement = (
    metricKey: string,
    elKey: string,
    patch: Partial<MetricDraft['elements'][number]>,
  ) =>
    setMetrics((prev) =>
      prev.map((m) =>
        m.key === metricKey
          ? { ...m, elements: m.elements.map((e) => (e.key === elKey ? { ...e, ...patch } : e)) }
          : m,
      ),
    );
  const addElement = (metricKey: string) =>
    setMetrics((prev) =>
      prev.map((m) =>
        m.key === metricKey ? { ...m, elements: [...m.elements, emptyElementDraft()] } : m,
      ),
    );
  const removeElement = (metricKey: string, elKey: string) =>
    setMetrics((prev) =>
      prev.map((m) =>
        m.key === metricKey ? { ...m, elements: m.elements.filter((e) => e.key !== elKey) } : m,
      ),
    );

  /** ⭐ P8.1：套用模板 —— 只回填「指标 + 要素」结构与任务形态，其余 meta 留空由用户填。
   *   ⚠️ 复用既有 TaskDesigner 状态（setMetrics），不新写一套表单；套用后用户仍可自由增删改。 */
  const applyTemplate = (structure: TemplateStructure, categoryId: string | null) => {
    const drafts = structureToMetricDrafts(structure);
    if (drafts.length > 0) setMetrics(drafts);
    if (categoryId) setMeta((p) => ({ ...p, categoryId }));
    setFormError('');
    setResult(null);
  };

  /** 表单校验（新建 / 编辑共用）；返回错误文案，null = 通过 */
  const validateForm = (): string | null => {
    if (!meta.name.trim()) {
      nameRef.current?.focus();
      return '请填写任务名称';
    }
    if (!meta.ownerId.trim()) return '请填写责任人';
    // 编辑模式不可改所属项目（projectId 不在编辑范围），故仅在新建时校验
    if (!isEdit && !meta.projectId) return '请选择所属项目';
    // ⭐ P7.7：计划结束不得早于开始（YYYY-MM-DD 字符串按字典序比较即可）
    if (meta.planStart && meta.planEnd && meta.planEnd < meta.planStart) {
      return '计划结束时间不得早于计划开始时间';
    }
    if (metrics.length === 0) return '至少需要 1 个指标';
    const metricWeightSum = metrics.reduce((s, m) => s + (Number(m.weight) || 0), 0);
    if (Math.round(metricWeightSum * 100) / 100 !== 100) {
      return `指标权重合计为 ${metricWeightSum}，必须等于 100 才能${isEdit ? '保存' : '创建'}`;
    }
    for (const m of metrics) {
      if (m.elements.length === 0) return `指标「${m.name}」至少需要 1 个要素`;
      const sum = m.elements.reduce((s, e) => s + (Number(e.weight) || 0), 0);
      if (sum !== 100) return `指标「${m.name}」的要素权重合计为 ${sum}，必须等于 100`;
    }
    return null;
  };

  const errText = (err: unknown, fallback: string): string =>
    err && typeof err === 'object' && 'message' in err
      ? String((err as { message?: unknown }).message ?? fallback)
      : fallback;

  /* ── 新建提交 ── */
  const onCreate = async () => {
    /* ⭐ P7.9：重复提交守卫 —— 提交中或已创建成功一律短路，双保险防连点 */
    if (submitting || submitted) return;
    setResult(null);
    const invalid = validateForm();
    if (invalid) {
      setFormError(invalid);
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      const res = await submitTaskDesign({ task: meta, metrics });
      setResult(res);
      /* ⭐ P7.9：成功后立即锁定，且不复位 submitting —— 跳转稍有延迟也不会再可点 */
      setSubmitted(true);
      /* P6.2：创建成功后主动失效星图/项目的任务树，列表即可刷新 */
      void queryClient.invalidateQueries({ queryKey: qk.taskTree(meta.projectId) });
      void queryClient.invalidateQueries({ queryKey: qk.project(meta.projectId) });

      /* ⭐ P7.9：创建成功 → 关闭新建抽屉、打开该任务详情抽屉 */
      closeTaskForm();
      openTask(res.taskId);

      /* ⚠️ 部分子资源失败不静默：任务主体已建成仍跳详情，但必须明确提示失败项 */
      if (res.failures.length > 0) {
        const lines = res.failures
          .slice(0, 6)
          .map((f) => `[${f.kind}] ${f.name}：${f.message}`)
          .join('\n');
        window.alert(
          `任务已创建，但 ${res.failures.length} 项指标或要素创建失败：\n` +
            lines +
            (res.failures.length > 6 ? `\n…等共 ${res.failures.length} 项` : ''),
        );
      }
    } catch (err) {
      setFormError(errText(err, '创建失败'));
      // 任务主体失败：解锁以便修正后重试（不可能已创建成功）
      setSubmitting(false);
    }
  };

  /* ── ⭐ P7.5 编辑保存（差异比对，见 api.ts updateTaskDesign） ── */
  const performUpdate = async () => {
    if (!editTaskId) return;
    /* ⭐ P7.9：重复提交守卫（与新建一致） */
    if (submitting || submitted) return;
    setDeleteConfirm(null);
    setFormError('');
    setEditFailures([]);
    setSubmitting(true);
    try {
      const res = await updateTaskDesign({
        taskId: editTaskId,
        task: meta,
        metrics,
        originalMetrics: originalRef.current,
      });

      // ⭐ 刷新：任务详情 / 进度 / 指标 / 列表 / 任务树 / 项目聚合
      void queryClient.invalidateQueries({ queryKey: qk.task(editTaskId) });
      void queryClient.invalidateQueries({ queryKey: qk.taskProgress(editTaskId) });
      void queryClient.invalidateQueries({ queryKey: qk.metrics(editTaskId) });
      void queryClient.invalidateQueries({ queryKey: qk.completeness(editTaskId) });
      void queryClient.invalidateQueries({ queryKey: qk.tasks() });
      void queryClient.invalidateQueries({ queryKey: qk.taskTree(meta.projectId) });
      void queryClient.invalidateQueries({ queryKey: qk.project(meta.projectId) });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });

      const w = window as unknown as Record<string, unknown>;
      w.__P75_UPDATED__ = {
        taskId: res.taskId,
        metricUpdated: res.metricUpdated,
        metricCreated: res.metricCreated,
        metricDeleted: res.metricDeleted,
        elementUpdated: res.elementUpdated,
        elementCreated: res.elementCreated,
        elementDeleted: res.elementDeleted,
        failures: res.failures.length,
      };

      if (res.failures.length === 0) {
        // ⭐ P7.9：全部成功 → 锁定按钮，关闭编辑抽屉并重新打开任务详情查看结果
        setSubmitted(true);
        closeTaskForm();
        openTask(editTaskId);
      } else {
        // 部分失败：留在表单内逐条展示，解锁按钮以便修正后重试
        setEditFailures(res.failures);
        setSubmitting(false);
      }
    } catch (err) {
      // ⚠️ 任务主体失败（如编辑中任务被他人提交 / 冻结）→ 原样展示后端错误，不吞错
      setFormError(errText(err, '保存失败'));
      setSubmitting(false);
    }
  };

  /* ── 保存入口：编辑模式先做删除二次确认，再执行更新 ── */
  const onSave = async () => {
    /* ⭐ P7.9：重复提交守卫 */
    if (submitting || submitted) return;
    setResult(null);
    setEditFailures([]);
    const invalid = validateForm();
    if (invalid) {
      setFormError(invalid);
      return;
    }
    setFormError('');

    const removals = countRemovals(metrics, originalRef.current);
    if (removals.metricCount > 0 || removals.elementCount > 0) {
      // ⚠️ 删除会级联清掉用户已填的完成度 / 质量 → 必须二次确认
      setDeleteConfirm(removals);
      return;
    }
    await performUpdate();
  };

  const totalWeight = metrics.reduce((s, m) => s + (Number(m.weight) || 0), 0);
  const elementCount = metrics.reduce((s, m) => s + m.elements.length, 0);
  /** 齐套率分母：勾选「需要附件」的要素总数 */
  const attachmentElementCount = metrics.reduce(
    (s, m) => s + m.elements.filter((e) => e.requireAttachment).length,
    0,
  );

  /* ⭐ P7.5：编辑模式加载态 / 失败态 —— 不得出现空白抽屉 */
  if (isEdit && loading) {
    return (
      <div className="td-page td-page--loading" data-testid="task-edit-loading">
        <Spinner label="加载任务数据…" />
      </div>
    );
  }
  if (isEdit && loadError) {
    return (
      <div className="td-page td-page--loading" data-testid="task-edit-loaderror">
        <div className="td-alert td-alert--err" role="alert">
          任务加载失败：{loadError}
        </div>
        <Button variant="secondary" onClick={closeTaskForm}>
          关闭
        </Button>
      </div>
    );
  }

  return (
    <div className="td-page">
      <header className="td-head">
        {/* ⭐ P7.14：标题条精简（去掉重复标题与说明文字），主操作按钮
            经 portal 投递到抽屉标题行右侧，与「新建任务」同排。 */}
        {actionsTarget &&
          createPortal(
            isEdit ? (
              <Button
                variant="primary"
                data-testid="save-task"
                disabled={submitting || submitted}
                onClick={onSave}
              >
                {submitting ? '保存中…' : '保存修改'}
              </Button>
            ) : (
              <>
                {/* ⭐ P8.1：从模板新建（仅新建模式）。套用模板后仍可自由增删改指标/要素 */}
                <Button
                  variant="secondary"
                  data-testid="task-from-template"
                  disabled={submitting || submitted}
                  title="从已有模板套用指标与要素结构"
                  onClick={() => setTemplatePickerOpen(true)}
                >
                  从模板新建
                </Button>
                <Button
                  variant="primary"
                  data-testid="create-task"
                  disabled={submitting || submitted}
                  onClick={onCreate}
                >
                  {submitting ? '创建中…' : '创建任务'}
                </Button>
              </>
            ),
            actionsTarget,
          )}

        <div className="td-meta">
          <label className="td-meta__fld td-meta__fld--project">
            <span className="td-meta__label">所属项目 *</span>
            <select
              className="td-input"
              data-testid="project"
              value={meta.projectId}
              disabled={isEdit}
              title={isEdit ? '任务不可跨项目迁移' : undefined}
              onChange={(e) => setMeta((p) => ({ ...p, projectId: e.target.value }))}
            >
              <option value="">请选择项目</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.provinceName ? `${p.provinceName} · ` : ''}
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="td-meta__fld td-meta__fld--grow">
            <span className="td-meta__label">任务名称 *</span>
            <input
              ref={nameRef}
              className="td-input"
              data-testid="task-name"
              name="taskName"
              placeholder="例如：华东智算中心一期 · 设备安装"
              value={meta.name}
              onChange={(e) => setMeta((p) => ({ ...p, name: e.target.value }))}
            />
          </label>
          <label className="td-meta__fld td-meta__fld--owner">
            <span className="td-meta__label">责任人 *</span>
            <UserPicker
              value={meta.ownerId}
              onChange={(id) => setMeta((p) => ({ ...p, ownerId: id }))}
              dataTestId="owner"
              placeholder="搜索姓名 / 用户名"
              required
              allowClear={false}
            />
          </label>
          <label className="td-meta__fld td-meta__fld--form">
            <span className="td-meta__label">任务形态</span>
            <select
              className="td-input"
              data-testid="category"
              value={meta.categoryId}
              disabled={categoriesQuery.isLoading || categoryOptions.length === 0}
              title={categoriesQuery.isError ? '任务形态字典加载失败，请刷新后重试' : undefined}
              onChange={(e) => setMeta((p) => ({ ...p, categoryId: e.target.value }))}
            >
              {categoriesQuery.isLoading ? (
                <option value={meta.categoryId}>加载中…</option>
              ) : (
                categoryOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              )}
            </select>
            {selectedCategory?.description && (
              <span
                className="td-meta__hint"
                data-testid="category-description"
                title={selectedCategory.description}
              >
                {selectedCategory.description}
              </span>
            )}
            {categoriesQuery.isError && (
              <span className="td-alert__hint" role="alert">
                任务形态加载失败：{categoriesQuery.error instanceof Error ? categoriesQuery.error.message : '请稍后重试'}
              </span>
            )}
          </label>
          <label className="td-meta__fld td-meta__fld--priority">
            <span className="td-meta__label">优先级</span>
            <select
              className="td-input"
              data-testid="priority"
              value={meta.priority}
              onChange={(e) => setMeta((p) => ({ ...p, priority: e.target.value }))}
            >
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {/* ⭐ P7.7：计划起止时间（逾期判定依赖「计划结束」） */}
          <label className="td-meta__fld td-meta__fld--start">
            <span className="td-meta__label">计划开始</span>
            <input
              type="date"
              className="td-input"
              data-testid="plan-start"
              value={meta.planStart}
              max={meta.planEnd || undefined}
              onChange={(e) => setMeta((p) => ({ ...p, planStart: e.target.value }))}
            />
          </label>
          <label className="td-meta__fld td-meta__fld--end">
            <span className="td-meta__label">计划结束</span>
            <input
              type="date"
              className="td-input"
              data-testid="plan-end"
              value={meta.planEnd}
              min={meta.planStart || undefined}
              onChange={(e) => setMeta((p) => ({ ...p, planEnd: e.target.value }))}
            />
          </label>
        </div>

        {formError && (
          <div className="td-alert td-alert--err" data-testid="form-alert" role="alert">
            {formError}
          </div>
        )}

        {result && result.failures.length === 0 && (
          <div
            className="td-alert td-alert--ok"
            data-testid="form-alert"
            data-task-id={result.taskId}
            role="status"
          >
            任务创建成功：已保存 {result.metricCreated} 项指标 · {result.elementCreated} 个要素 · 需附件要素{' '}
            {attachmentElementCount} 个。
            <button type="button" className="td-alert__link" onClick={() => openTask(result.taskId)}>
              查看任务详情
            </button>
          </div>
        )}

        {result && result.failures.length > 0 && (
          <div
            className="td-alert td-alert--err"
            data-testid="form-alert"
            data-task-id={result.taskId}
            role="alert"
          >
            <b>
              任务已创建（#{result.taskId}），但 {result.failures.length} 项保存失败
            </b>
            <ul className="td-alert__list">
              {result.failures.slice(0, 6).map((f, i) => (
                <li key={`${f.kind}-${i}`}>
                  [{f.kind}] {f.name}：{f.message}
                </li>
              ))}
            </ul>
            {/*
              P6.50：权限类失败是 DB 权限点缺失/未授权导致，任务详情页的契约列是
              另一套组件（详情页无派生契约入口），「前往任务详情继续编辑」会误导用户白跑。
              故权限类失败只给「联系管理员开通」引导；非权限失败才保留详情页入口。
            */}
            {result.failures.some(isPermissionFailure) && (
              <p className="td-alert__hint" data-testid="perm-hint">
                该项需要相应权限，请联系管理员开通后重试
              </p>
            )}
            {result.failures.some((f) => !isPermissionFailure(f)) && (
              <button
                type="button"
                className="td-alert__link"
                onClick={() => openTask(result.taskId)}
              >
                前往任务详情继续编辑
              </button>
            )}
          </div>
        )}

        {/* ⭐ P7.5：编辑保存部分失败 —— 不吞错，逐条展示 */}
        {isEdit && editFailures.length > 0 && (
          <div
            className="td-alert td-alert--err"
            data-testid="form-alert"
            data-task-id={editTaskId ?? undefined}
            role="alert"
          >
            <b>保存完成，但 {editFailures.length} 项失败（可修正后重试）</b>
            <ul className="td-alert__list">
              {editFailures.slice(0, 8).map((f, i) => (
                <li key={`${f.kind}-${i}`}>
                  [{f.kind}] {f.name}：{f.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </header>

      <div className="td-cols td-cols--single">
        <MetricDesigner
          metrics={metrics}
          onMetricChange={changeMetric}
          onMetricRemove={removeMetric}
          onMetricDuplicate={duplicateMetric}
          onElementChange={changeElement}
          onElementAdd={addElement}
          onElementRemove={removeElement}
          onAddMetric={addMetric}
        />
      </div>

      <div className="td-foot">
        指标权重合计 {Math.round(totalWeight * 100) / 100} / 100 · {elementCount} 个要素 · 完成度与质量由系统按要素加权计算
      </div>

      {/* ⭐ P7.5：删除二次确认 —— 防误删（级联删除要素与已填完成度/质量） */}
      <Modal
        open={Boolean(deleteConfirm)}
        danger
        size="sm"
        title="确认删除指标 / 要素？"
        onClose={() => setDeleteConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
              取消
            </Button>
            <Button
              variant="danger"
              data-testid="delete-confirm-ok"
              disabled={submitting}
              onClick={() => void performUpdate()}
            >
              确认删除并保存
            </Button>
          </>
        }
      >
        <p data-testid="delete-confirm">
          将删除 <b>{deleteConfirm?.metricCount ?? 0}</b> 个指标、
          <b>{deleteConfirm?.elementCount ?? 0}</b> 个要素；其下已填报的完成度与质量数据将一并删除，
          且不可恢复。确认继续？
        </p>
      </Modal>

      {/* ⭐ P8.1：从模板新建 —— 选中后把结构回填到 metrics（仅新建模式） */}
      <TemplatePickerDialog
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onSelect={applyTemplate}
      />
    </div>
  );
}
